import { useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * Hover/focus tooltip to the right of its child — for icon-only navigation
 * (app rail, collapsed server sidebar). Rendered in a body portal with fixed
 * positioning so scrolling/overflow-hidden sidebars can't clip it.
 */
export function Tooltip({
  label,
  children,
  disabled = false,
}: {
  label: ReactNode;
  children: ReactNode;
  disabled?: boolean;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const show = () => {
    const r = ref.current?.getBoundingClientRect();
    if (r) setPos({ top: r.top + r.height / 2, left: r.right + 10 });
  };
  const hide = () => setPos(null);

  return (
    <span
      ref={ref}
      className="inline-flex"
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      onClick={hide}
    >
      {children}
      {!disabled &&
        pos &&
        createPortal(
          <div
            role="tooltip"
            className="glass-float pointer-events-none fixed z-[100] -translate-y-1/2 whitespace-nowrap rounded-md border bg-card px-2 py-1 text-xs font-medium text-card-foreground shadow-md"
            style={{ top: pos.top, left: pos.left }}
          >
            {label}
          </div>,
          document.body
        )}
    </span>
  );
}
